/**
 * Gemini Keys Admin API
 * Gemini Key-ების ადმინისტრირების API
 */

import { get, post, put, del } from '@aiia/portal-core';

export interface GeminiKey {
  id: string;
  name: string;
  model: string;
  status: 'ACTIVE' | 'DISABLED' | 'RATE_LIMITED';
  dailyLimit: number;
  dailyUsageCount: number;
  dailyResetAt?: string;
  lastUsedAt?: string;
  assignedUsersCount: number;
  createdBy: string;
  createdByName: string;
  createdAt: string;
  updatedAt: string;
}

export interface GeminiKeyStats {
  totalKeys: number;
  activeKeys: number;
  disabledKeys: number;
  rateLimitedKeys: number;
  totalUsage: number;
  totalLimit: number;
  utilizationPercent: number;
  totalAssignments: number;
}

export interface GeminiAuditLog {
  id: string;
  action: string;
  keyId?: string;
  keyName?: string;
  userId?: string;
  oldValue?: string;
  newValue?: string;
  reason?: string;
  affectedUsersCount?: number;
  performedBy: string;
  performedByName: string;
  performedAt: string;
}

export interface Assignment {
  userId: string;
  assignedAt: string;
  assignedByName: string;
}

export interface CreateGeminiKeyRequest {
  name: string;
  apiKey: string;
  dailyLimit?: number;
  model?: string;
}

// Available Gemini models
export const GEMINI_MODELS = [
  { value: 'gemini-2.5-flash', label: 'Gemini 2.5 Flash' },
] as const;

export interface UpdateGeminiKeyRequest {
  name?: string;
  apiKey?: string;
  dailyLimit?: number;
  reason: string;
}

export interface TestKeyResult {
  valid: boolean;
  message: string;
}

// Get all Gemini keys
export const getGeminiKeys = () =>
  get<GeminiKey[]>('/admin/gemini-keys');

// Get Gemini key by ID
export const getGeminiKey = (keyId: string) =>
  get<GeminiKey>(`/admin/gemini-keys/${keyId}`);

// Create Gemini key
export const createGeminiKey = (data: CreateGeminiKeyRequest) =>
  post<GeminiKey>('/admin/gemini-keys', data);

// Update Gemini key
export const updateGeminiKey = (keyId: string, data: UpdateGeminiKeyRequest) =>
  put<GeminiKey>(`/admin/gemini-keys/${keyId}`, data);

// Disable Gemini key
export const disableGeminiKey = (keyId: string, reason: string) =>
  post<void>(`/admin/gemini-keys/${keyId}/disable`, { reason });

// Enable Gemini key
export const enableGeminiKey = (keyId: string) =>
  post<void>(`/admin/gemini-keys/${keyId}/enable`, {});

// Delete Gemini key
export const deleteGeminiKey = (keyId: string, reason?: string) =>
  del<void>(`/admin/gemini-keys/${keyId}${reason ? `?reason=${encodeURIComponent(reason)}` : ''}`);

// Get key assignments
export const getKeyAssignments = (keyId: string) =>
  get<Assignment[]>(`/admin/gemini-keys/${keyId}/assignments`);

// Assign user to key
export const assignUserToKey = (userId: string, keyId: string, userName?: string) =>
  post<void>('/admin/gemini-keys/assign', { userId, keyId, userName });

// Get stats
export const getGeminiStats = () =>
  get<GeminiKeyStats>('/admin/gemini-keys/stats');

// Get audit logs
export const getGeminiAuditLogs = (keyId?: string, hours: number = 24) =>
  get<GeminiAuditLog[]>(`/admin/gemini-keys/audit?hours=${hours}${keyId ? `&keyId=${keyId}` : ''}`);

// Test API key
export const testGeminiKey = (apiKey: string) =>
  post<TestKeyResult>('/admin/gemini-keys/test', { apiKey });
